// @flow
import React, { Fragment } from 'react';
import { get } from 'lodash';

import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';

import Image from 'Components/frameworks/image/Image.jsx';
import { ResponsiveTabs } from 'Components/frameworks/responsive-tabs/ResponsiveTabs.jsx';
import { Tag } from 'Components/frameworks/tag/Tag.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

type ActivityComponentType = {
  activity: Activity,
  index: number,
  length: number,
};

type Props = {
  activities: any,
  prestations: Array<any>,
};

function getYearsOld(activity) {
  if (activity.adultsOnly) {
    return t('productWellness.ages.adultsOnly');
  }

  if (activity.ageInMonths && activity.ageInMonths.min) {
    return t('productWellness.year', { count: Math.floor(activity.ageInMonths.min / 12) });
  }

  return t('productWellness.ages.all');
}

export function ActivityComponent({ activity, index, length }: ActivityComponentType) {
  return (
    <article data-testid="ProductWellness-activity">
      {activity.image && (
        <div data-testid="ProductWellnessImageActivity">
          <Image imageUrl={activity.image} ratioKey={ratioUtils.M} />
        </div>
      )}
      {activity.name && (
        <Title className="mb-2" hLevel={3} theme="title-sans-3">
          {activity.name}
          <small className="rtl:ml-1 font-normal text-sm">{activity.duration}</small>
        </Title>
      )}
      <Tag
        color={activity.included ? COLORS.KIWI : COLORS.AMBER}
        {...(activity.included ? { iconRight: ICONS.CHECK } : null)}
        label={activity.included ? t('productWellness.included') : t('productWellness.onDemand')}
        theme="outlined"
      />
      <div className="text-1">
        <p>
          <strong>{t('productWellness.ages.label')}</strong> : <span>{getYearsOld(activity)}</span>
        </p>
      </div>
      <Text text={activity.description} />
      {index < length - 1 && (
        <hr className="block border-0 border-solid border-gray-light border-t-1 h-px my-4 mx-0 p-0" />
      )}
    </article>
  );
}

function getTabsItems(prestations = []) {
  return prestations.map(({ activities, id, name }, index) => {
    return {
      header: {
        title: name,
      },
      content: [
        <section data-testid="ProductWellness-prestation" className="py-5" key={id || index}>
          {activities.map(
            (activity, activityIndex) =>
              activity && (
                <ActivityComponent
                  activity={activity}
                  index={activityIndex}
                  key={activity.id}
                  length={activities.length}
                />
              ),
          )}
        </section>,
      ],
    };
  });
}

export function ProductWellnessLegacy(props: Props) {
  const { logo } = props.activities;

  return (
    <div className="pt-10 pb-8 sm:flex sm:items-start">
      <Title className="sm:w-1/5 mb-5" hLevel={3} theme="title-sans-2">
        {t('productWellness.title')}
      </Title>
      <div className="sm:w-4/5 sm:px-5">
        <div className="sm:flex items-start pb-5">
          <div>
            <Text text={get(props, 'activities.description', '')} />
            {!!get(props, 'activities.equipments', []).length && (
              <Fragment>
                <Title className="mt-2" hLevel={4} theme="title-sans-3">
                  {t('productWellness.equipements')}
                </Title>
                <ul className="reset-list mt-2 text-base sm:colum-count-2">
                  {props.activities &&
                    props.activities.equipments.map(({ name }) => (
                      <li data-testid="WellnessIntroductionItem" className="list-disc" key={name}>
                        {name}
                      </li>
                    ))}
                </ul>
              </Fragment>
            )}
          </div>
          {!!logo && (
            <div data-testid="ProductWellnessLogo">
              <Image className="mt-5 sm:w-1/5" dontChangeAnything imageUrl={logo} />
            </div>
          )}
        </div>
        <ResponsiveTabs
          areAllCollapsable
          items={getTabsItems(props.prestations)}
          expandIconName={ICONS.PLUS}
        />
        <Text text={get(props, 'activities.footnotes', '')} theme="text-sans-2" />
      </div>
    </div>
  );
}
